import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  FileArrowUp, FilePdf, Trash, CheckCircle, Spinner,
  XCircle, Plus, Hospital
} from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadDialog({ open, onOpenChange, onClaimCreated, existingClaimId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [claimId, setClaimId] = useState(existingClaimId || null);
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState("");
  const [policyStart, setPolicyStart] = useState("");
  const [policyEnd, setPolicyEnd] = useState("");
  const [sumInsured, setSumInsured] = useState("");
  const fileInputRef = useRef(null);

  // Fetch hospitals for dropdown
  useEffect(() => {
    if (open && !existingClaimId) {
      axios.get(`${API}/hospitals`, { timeout: 10000 }).then(res => {
        setHospitals(res.data || []);
      }).catch(() => {});
    }
  }, [open, existingClaimId]);

  const addFiles = (newFiles) => {
    const validFiles = Array.from(newFiles).filter(f => 
      f.name.toLowerCase().endsWith(".pdf") || f.name.toLowerCase().endsWith(".json")
    );
    const entries = validFiles.map(f => ({
      file: f,
      docType: f.name.toLowerCase().endsWith(".json") ? "json_policy" : "claim",
      status: "pending",
      progress: 0,
      result: null,
      error: null
    }));
    setFiles(prev => [...prev, ...entries]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const setDocType = (idx, type) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, docType: type } : f));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    setUploading(true);
    let currentClaimId = claimId || existingClaimId;

    for (let i = 0; i < files.length; i++) {
      const entry = files[i];
      if (entry.status === "done") continue;

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: "uploading", progress: 10 } : f));

      const formData = new FormData();
      formData.append("file", entry.file);

      try {
        let res;
        const isJson = entry.file.name.toLowerCase().endsWith(".json");

        if (isJson && currentClaimId) {
          // JSON file → upload via json endpoint
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: 50 } : f));
          res = await axios.post(`${API}/claims/${currentClaimId}/upload-json`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        } else if (isJson && !currentClaimId) {
          // JSON first — need a PDF first, skip for now and mark as needs-claim
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: "error", error: "Upload a PDF first to create the claim, then add JSON" } : f));
          continue;
        } else if (!currentClaimId) {
          // First PDF — create the claim with hospital
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: 30 } : f));
          let uploadUrl = `${API}/claims/upload`;
          const params = new URLSearchParams();
          if (selectedHospital) params.append("hospital", selectedHospital);
          if (policyStart) params.append("policy_start", policyStart);
          if (policyEnd) params.append("policy_end", policyEnd);
          if (sumInsured) params.append("sum_insured", sumInsured);
          if (params.toString()) uploadUrl += `?${params.toString()}`;
          res = await axios.post(uploadUrl, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000, // 5 min for large PDFs
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded / (e.total || 1)) * 70) + 10;
              setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: Math.min(pct, 80) } : f));
            }
          });
          currentClaimId = res.data.id;
          setClaimId(currentClaimId);
        } else {
          // Subsequent PDF — add to existing claim
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: 30 } : f));
          res = await axios.post(
            `${API}/claims/${currentClaimId}/add-file?doc_type=${entry.docType}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 300000, // 5 min for large PDFs
              onUploadProgress: (e) => {
                const pct = Math.round((e.loaded / (e.total || 1)) * 70) + 10;
                setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: Math.min(pct, 80) } : f));
              }
            }
          );
        }

        setFiles(prev => prev.map((f, idx) => idx === i ? {
          ...f, status: "done", progress: 100, result: res.data
        } : f));
      } catch (err) {
        const errMsg = err?.response?.data?.detail ||
          err?.code === "ECONNABORTED" ? "Upload timed out — large files may take longer. Please try again." :
          err?.message || "Upload failed";
        setFiles(prev => prev.map((f, idx) => idx === i ? {
          ...f, status: "error", progress: 0,
          error: errMsg
        } : f));
      }
    }

    setUploading(false);
    if (currentClaimId) {
      onClaimCreated?.(currentClaimId);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setClaimId(existingClaimId || null);
      setSelectedHospital("");
      setPolicyStart("");
      setPolicyEnd("");
      setSumInsured("");
      onOpenChange(false);
    }
  };

  const allDone = files.length > 0 && files.every(f => f.status === "done");
  const hasFiles = files.length > 0;
  const totalPages = files.reduce((s, f) => s + (f.result?.total_pages || f.result?.new_pages || 0), 0);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {existingClaimId ? "Add More Files" : claimId ? "Files Added" : "Create New Claim"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Hospital selector — only for new claims */}
          {!existingClaimId && !claimId && !allDone && hospitals.length > 0 && (
            <div data-testid="hospital-selector-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5">
                <Hospital size={13} className="text-gray-400" />
                Select Hospital (for tariff)
              </Label>
              <Select value={selectedHospital} onValueChange={setSelectedHospital}>
                <SelectTrigger className="h-9" data-testid="hospital-select">
                  <SelectValue placeholder="Choose hospital to apply correct tariff..." />
                </SelectTrigger>
                <SelectContent>
                  {hospitals.map((h, idx) => (
                    <SelectItem key={idx} value={h.hospital_name || h.name || `hospital-${idx}`}>
                      <span className="flex items-center gap-2">
                        <span>{h.hospital_name || h.name}</span>
                        {h.city && <span className="text-xs text-gray-400">({h.city})</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Policy fields — only for new claims */}
          {!existingClaimId && !claimId && !allDone && (
            <div className="grid grid-cols-3 gap-3" data-testid="policy-fields">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">
                  Policy Inception Date
                </Label>
                <Input
                  type="date"
                  value={policyStart}
                  onChange={e => setPolicyStart(e.target.value)}
                  className="h-9"
                  data-testid="policy-start-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">
                  Policy Expiry Date
                </Label>
                <Input
                  type="date"
                  value={policyEnd}
                  onChange={e => setPolicyEnd(e.target.value)}
                  className="h-9"
                  data-testid="policy-end-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">
                  Available Sum Insured (INR)
                </Label>
                <Input
                  type="number"
                  value={sumInsured}
                  onChange={e => setSumInsured(e.target.value)}
                  placeholder="e.g. 500000"
                  className="h-9"
                  data-testid="sum-insured-input"
                />
              </div>
            </div>
          )}

          {/* Drop zone */}
          {!allDone && (
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:border-[#002FA7] hover:bg-blue-50/30 transition-colors"
              data-testid="drop-zone"
            >
              <FileArrowUp size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Drag & drop PDF or JSON files here, or click to browse
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PDF: claim documents, policy documents | JSON: patient/policy data
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.json"
                multiple
                className="hidden"
                onChange={e => addFiles(e.target.files)}
                data-testid="file-input"
              />
            </div>
          )}

          {/* File list */}
          {hasFiles && (
            <div className="space-y-2">
              {files.map((entry, idx) => (
                <div
                  key={idx}
                  className={`border rounded-md p-3 ${
                    entry.status === "done" ? "border-green-200 bg-green-50/30" :
                    entry.status === "error" ? "border-red-200 bg-red-50/30" :
                    "border-gray-200 bg-white"
                  }`}
                  data-testid={`upload-file-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                      entry.status === "done" ? "bg-green-100" :
                      entry.status === "error" ? "bg-red-100" :
                      "bg-gray-100"
                    }`}>
                      {entry.status === "done" ? <CheckCircle size={18} weight="fill" className="text-green-600" /> :
                       entry.status === "error" ? <XCircle size={18} weight="fill" className="text-red-500" /> :
                       entry.status === "uploading" ? <Spinner size={18} className="text-[#002FA7] animate-spin" /> :
                       <FilePdf size={18} className="text-gray-500" />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{entry.file.name}</p>
                      <p className="text-xs text-gray-400">
                        {formatSize(entry.file.size)}
                        {entry.result?.total_pages && ` — ${entry.result.total_pages} pages`}
                        {entry.result?.new_pages && ` — ${entry.result.new_pages} pages added`}
                      </p>
                    </div>

                    {/* Doc type selector */}
                    {entry.status === "pending" && (
                      <Select value={entry.docType} onValueChange={v => setDocType(idx, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid={`doc-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="claim">Claim Doc</SelectItem>
                          <SelectItem value="policy">Policy PDF</SelectItem>
                          <SelectItem value="json_policy">JSON Data</SelectItem>
                          <SelectItem value="supporting">Supporting</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    {/* Status badge */}
                    {entry.status === "done" && entry.docType && (
                      <Badge variant="outline" className="text-xs capitalize bg-green-50 text-green-700 border-green-200">
                        {entry.docType}
                      </Badge>
                    )}

                    {/* Remove */}
                    {entry.status === "pending" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(idx)}
                        className="text-gray-400 hover:text-red-500 h-7 w-7 p-0"
                        data-testid={`remove-file-${idx}`}
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>

                  {/* Progress bar */}
                  {(entry.status === "uploading" || (entry.status === "done" && entry.progress > 0)) && (
                    <Progress value={entry.progress} className="h-1.5 mt-2" />
                  )}

                  {/* Error message */}
                  {entry.status === "error" && (
                    <p className="text-xs text-red-500 mt-1">{entry.error}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add more files button */}
          {hasFiles && !allDone && !uploading && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border border-dashed border-gray-300 rounded-md py-2 text-sm text-gray-500 hover:text-[#002FA7] hover:border-[#002FA7] transition-colors flex items-center justify-center gap-1.5"
              data-testid="add-more-files"
            >
              <Plus size={14} /> Add more files
            </button>
          )}

          {/* Summary when all done */}
          {allDone && (
            <div className="border border-green-200 bg-green-50 rounded-md p-4 text-center">
              <CheckCircle size={28} weight="fill" className="mx-auto text-green-600 mb-2" />
              <p className="text-sm font-medium text-green-800">
                {files.length} file{files.length > 1 ? "s" : ""} uploaded — {totalPages} total pages
              </p>
              <p className="text-xs text-green-600 mt-1">
                Click &ldquo;View Claim&rdquo; to open and process
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!allDone ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={uploading} data-testid="cancel-upload">
                Cancel
              </Button>
              <Button
                onClick={handleUploadAll}
                disabled={!hasFiles || uploading || files.every(f => f.status === "done")}
                className="bg-[#002FA7] hover:bg-blue-800 text-white gap-2"
                data-testid="start-upload"
              >
                {uploading ? (
                  <><Spinner size={16} className="animate-spin" /> Uploading...</>
                ) : (
                  <><FileArrowUp size={16} /> Upload {files.filter(f => f.status === "pending").length} file{files.filter(f => f.status === "pending").length !== 1 ? "s" : ""}</>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => { handleClose(); }}
              className="bg-[#002FA7] hover:bg-blue-800 text-white"
              data-testid="view-claim-btn"
            >
              View Claim
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
